import React, { useEffect, useState } from 'react';
import { trackEsimEvent } from './esimAnalytics';
import { planSlug, qrPngOptions } from './esimQrFile';

interface Props {
  activationCode?: string;
  planName?: string;
  ready: boolean;
  notice?: 'full' | 'short';
  layout?: 'stack' | 'split';
  onSaved?: () => void;
  onLive?: (text: string) => void;
  children?: React.ReactNode;
}

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    const area = document.createElement('textarea');
    area.value = value;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch { ok = false; }
    area.remove();
    return ok;
  }
}

export const EsimInstallPanel: React.FC<Props> = ({
  activationCode = '',
  planName,
  ready,
  notice = 'full',
  layout = 'stack',
  onSaved,
  onLive,
  children,
}) => {
  const [qrSvg, setQrSvg] = useState('');
  const [copied, setCopied] = useState<'all' | 'smdp' | 'code' | null>(null);
  const lpa = ready ? activationCode : '';
  const parts = lpa.split('$');
  const smdp = parts[1] || '';
  const code = parts[2] || '';

  useEffect(() => {
    if (!lpa) {
      setQrSvg('');
      return;
    }
    let cancel = false;
    void import('qrcode').then((QR) => {
      const lib = (QR as { toString?: typeof import('qrcode').toString; default?: typeof import('qrcode') }).toString
        ? QR as typeof import('qrcode')
        : (QR as { default: typeof import('qrcode') }).default;
      return lib.toString(lpa, { type: 'svg', margin: 1, errorCorrectionLevel: 'M' });
    }).then((svg) => {
      if (!cancel) setQrSvg(svg);
    }).catch(() => undefined);
    return () => { cancel = true; };
  }, [lpa]);

  const flash = async (which: 'all' | 'smdp' | 'code', value: string) => {
    if (!value || !(await copyText(value))) return;
    setCopied(which);
    onSaved?.();
    onLive?.('Copied');
    trackEsimEvent({ name: 'esim_code_copied', target: which });
    window.setTimeout(() => setCopied(null), 1800);
  };

  const download = async () => {
    if (!lpa) return;
    const loaded = await import('qrcode');
    const QR = (loaded as { toDataURL?: typeof import('qrcode').toDataURL }).toDataURL
      ? loaded as typeof import('qrcode')
      : (loaded as { default: typeof import('qrcode') }).default;
    const url = await QR.toDataURL(lpa, qrPngOptions());
    const link = document.createElement('a');
    link.href = url;
    link.download = `${planSlug(planName || 'canada-esim')}-esim-qr.png`;
    link.click();
    onSaved?.();
    trackEsimEvent({ name: 'esim_qr_downloaded' });
  };

  const qr = (
    <div className="esim-qr-stage">
      <div className="esim-qr-tile">
        {!ready ? <div className="esim-skel" /> : null}
        {ready ? (
          <div
              className="esim-qr"
            role="img"
            aria-label="eSIM activation QR code"
            dangerouslySetInnerHTML={{ __html: qrSvg }}
          />
        ) : null}
        <div className="esim-sheen" />
      </div>
    </div>
  );
  const actions = (
    <div className="esim-actions">
      <button type="button" className="esim-btn esim-btn-primary" disabled={!ready} onClick={() => { void download(); }}><DownloadIcon />Download QR</button>
      <button type="button" className={copied === 'all' ? 'esim-btn esim-btn-ghost is-done' : 'esim-btn esim-btn-ghost'} disabled={!ready} onClick={() => { void flash('all', lpa); }}>
        {copied === 'all' ? <><CheckIcon />Copied</> : <><CopyIcon />Copy code</>}
      </button>
    </div>
  );
  const manual = ready && smdp && code ? (
    <details className="esim-manual">
      <summary>Install manually <ChevronIcon /></summary>
      <div className="esim-field">
        <div><dt>SM-DP+ address</dt><dd>{smdp}</dd></div>
        <button type="button" className={copied === 'smdp' ? 'esim-icobtn is-done' : 'esim-icobtn'} aria-label="Copy SM-DP+ address" onClick={() => { void flash('smdp', smdp); }}>{copied === 'smdp' ? <CheckIcon /> : <CopyIcon />}</button>
      </div>
      <div className="esim-field">
        <div><dt>Activation code</dt><dd>{code}</dd></div>
        <button type="button" className={copied === 'code' ? 'esim-icobtn is-done' : 'esim-icobtn'} aria-label="Copy activation code" onClick={() => { void flash('code', code); }}>{copied === 'code' ? <CheckIcon /> : <CopyIcon />}</button>
      </div>
    </details>
  ) : null;
  const note = ready ? (
    <p className="esim-notice">
      <WarnIcon />
      <span>
        {notice === 'short'
          ? <><b>Keep this QR private.</b> It installs on one device, once.</>
          : <><b>This works on one phone, once.</b> Don’t share the QR. If you delete the eSIM later, you usually cannot add it again.</>}
      </span>
    </p>
  ) : null;

  if (layout === 'split') {
    return (
      <div className="esim-orders-body">
        <div className="esim-orders-qcol">{qr}{actions}</div>
        <div>{children}{manual}{note}</div>
      </div>
    );
  }

  return (
    <>
      {qr}
      {children}
      {actions}
      {manual}
      {note}
    </>
  );
};

function DownloadIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 4v11M7 11l5 5 5-5M5 20h14" /></svg>;
}
function CopyIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V6a2 2 0 0 1 2-2h9" /></svg>;
}
function CheckIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>;
}
function ChevronIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M6 9l6 6 6-6" /></svg>;
}
function WarnIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 3l9 16H3z" /><path d="M12 10v4M12 17h.01" /></svg>;
}
